/**
 * Backend spec 006 (sheet D5) — the quality of a spoken answer, measured in the
 * browser. The server judges again with the same thresholds; this copy only lets
 * the contributor re-record before sending.
 */

export const AUDIO_QUALITY_VERSION = 1;

/** Must match AUDIO_QUALITY_THRESHOLDS on the backend. */
export const AUDIO_QUALITY_THRESHOLDS = {
  maxClippingRatio: 0.01,
  maxSilenceRatio: 0.8,
  minSpeechDbfs: -40,
  minSnrDb: 10,
} as const;

export const AUDIO_QUALITY_ISSUES = ['CLIPPED', 'MOSTLY_SILENT', 'TOO_QUIET', 'NOISY'] as const;
export type AudioQualityIssue = (typeof AUDIO_QUALITY_ISSUES)[number];

export interface AudioQualityMetrics {
  version: typeof AUDIO_QUALITY_VERSION;
  peakDbfs: number;
  speechDbfs: number;
  noiseFloorDbfs: number;
  snrDb: number;
  clippingRatio: number;
  silenceRatio: number;
  durationMs: number;
}

const FRAME_MS = 20;
const FLOOR_DB = -100;
const SILENCE_DB = -50;
const CLIP_LEVEL = 0.999;

const toDb = (amplitude: number) => (amplitude > 0 ? Math.max(FLOOR_DB, 20 * Math.log10(amplitude)) : FLOOR_DB);
const round = (value: number, step: number) => Math.round(value / step) * step;

/** Value at quantile q of an ascending-sorted list. */
const quantile = (sorted: number[], q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] ?? FLOOR_DB;

/** O(n) over the samples, then O(f log f) over the 20 ms frames. */
export function measureAudioQuality(samples: Float32Array, sampleRate: number): AudioQualityMetrics {
  const frameSize = Math.max(1, Math.round((sampleRate * FRAME_MS) / 1000));
  const frameDb: number[] = [];
  let peak = 0;
  let clipped = 0;
  for (let start = 0; start < samples.length; start += frameSize) {
    const end = Math.min(samples.length, start + frameSize);
    let energy = 0;
    for (let i = start; i < end; i += 1) {
      const value = Math.abs(samples[i] ?? 0);
      energy += value * value;
      if (value > peak) peak = value;
      if (value >= CLIP_LEVEL) clipped += 1;
    }
    frameDb.push(toDb(Math.sqrt(energy / (end - start))));
  }
  const sorted = [...frameDb].sort((a, b) => a - b);
  const speechDbfs = quantile(sorted, 0.9);
  const noiseFloorDbfs = quantile(sorted, 0.1);
  const silent = frameDb.filter((db) => db < SILENCE_DB).length;
  return {
    version: AUDIO_QUALITY_VERSION,
    peakDbfs: round(Math.min(0, toDb(peak)), 0.1),
    speechDbfs: round(speechDbfs, 0.1),
    noiseFloorDbfs: round(noiseFloorDbfs, 0.1),
    snrDb: round(Math.max(0, speechDbfs - noiseFloorDbfs), 0.1),
    clippingRatio: samples.length === 0 ? 0 : round(clipped / samples.length, 0.001),
    silenceRatio: frameDb.length === 0 ? 1 : round(silent / frameDb.length, 0.001),
    durationMs: Math.round((samples.length / sampleRate) * 1000),
  };
}

export function audioQualityIssues(m: AudioQualityMetrics): AudioQualityIssue[] {
  const t = AUDIO_QUALITY_THRESHOLDS;
  const issues: AudioQualityIssue[] = [];
  if (m.clippingRatio > t.maxClippingRatio) issues.push('CLIPPED');
  if (m.silenceRatio > t.maxSilenceRatio) issues.push('MOSTLY_SILENT');
  if (m.speechDbfs < t.minSpeechDbfs) issues.push('TOO_QUIET');
  if (m.snrDb < t.minSnrDb) issues.push('NOISY');
  return issues;
}

/** Reads the reasons of a 422 AUDIO_QUALITY_REJECTED body. */
export function rejectedIssues(body: unknown): AudioQualityIssue[] {
  if (typeof body !== 'object' || body === null || !('issues' in body) || !Array.isArray(body.issues)) return [];
  return body.issues.filter((issue): issue is AudioQualityIssue => AUDIO_QUALITY_ISSUES.some((known) => known === issue));
}
